import store from '../store'
import router from '../router'

function getRuntimeOriginParts() {
  const protocol = window.location.protocol || 'http:'
  const hostname = window.location.hostname || ''
  const resolvedProtocol = protocol === 'file:' ? 'http:' : protocol
  const resolvedHostname = hostname || 'localhost'
  return { protocol: resolvedProtocol, hostname: resolvedHostname }
}

export function resolveBackendOrigin() {
  const { hostname } = getRuntimeOriginParts()

  // Local / file open → local backend
  if (
    window.location.protocol === 'file:' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  ) {
    return 'http://localhost:3005'
  }

  // Production User Mini App → API subdomain
  if (hostname === 'jobs.theminasoft.com' || hostname.endsWith('.theminasoft.com')) {
    return 'https://api.jobs.theminasoft.com'
  }

  // Other hosts (LAN IP, preview, etc.): same host on backend port
  const { protocol } = getRuntimeOriginParts()
  const pagePort = window.location.port
  if (!pagePort || pagePort === '80' || pagePort === '443') {
    return `${protocol}//${hostname}`
  }
  if (pagePort === '3005') {
    return `${protocol}//${hostname}:3005`
  }
  return `${protocol}//${hostname}:3005`
}

export const API_BASE_URL = `${resolveBackendOrigin()}/api`
export const UPLOADS_BASE_URL = `${resolveBackendOrigin()}/uploads`

export function resolveUploadUrl(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('blob:') || raw.startsWith('data:')) {
    return raw
  }
  return `${UPLOADS_BASE_URL}/${raw.replace(/^\/?uploads\//i, '')}`
}

export const handleSessionExpired = async () => {
  try {
    if (store?.dispatch) await store.dispatch('logout')
    router.push({ name: 'welcome', query: { session: 'expired' } })
  } catch (err) {
    console.error('Error in handleSessionExpired:', err)
  }
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    )
    return JSON.parse(json)
  } catch {
    return null
  }
}

function shouldRefreshToken(token) {
  const payload = decodeJwtPayload(token)
  const exp = Number(payload?.exp || 0)
  if (!exp) return false
  const nowSec = Math.floor(Date.now() / 1000)
  return exp - nowSec <= 2 * 60
}

function isPublicAuthEndpoint(cleanEndpoint) {
  return (
    cleanEndpoint.startsWith('/auth/telegram') ||
    cleanEndpoint.startsWith('/auth/admin/login') ||
    cleanEndpoint.startsWith('/jobs/sectors') ||
    cleanEndpoint.startsWith('/health')
  )
}

export const apiFetch = async (endpoint, options = {}) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${cleanEndpoint}`

  if (!store.state.token && !isPublicAuthEndpoint(cleanEndpoint)) {
    throw new Error('Not authenticated')
  }

  try {
    const last = parseInt(localStorage.getItem('Ahun_last_activity') || '0', 10)
    const INACTIVITY_LIMIT_MS = 30 * 60 * 1000
    if (last && Date.now() - last > INACTIVITY_LIMIT_MS) {
      await handleSessionExpired()
      throw new Error('Session expired due to inactivity')
    }
    localStorage.setItem('Ahun_last_activity', String(Date.now()))
  } catch (err) {
    if (err.message?.includes('Session expired')) throw err
  }

  const headers = { ...options.headers }
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  if (store.state.token && shouldRefreshToken(store.state.token) && !cleanEndpoint.startsWith('/auth/refresh')) {
    const refreshed = await store.dispatch('refreshToken')
    if (!refreshed) {
      await handleSessionExpired()
      throw new Error('Session expired')
    }
  }

  if (store.state.token) {
    headers.Authorization = `Bearer ${store.state.token}`
  }

  const config = { ...options, headers }

  try {
    const response = await fetch(url, config)
    if (response.status === 401) {
      const isAuthRoute = url.includes('/auth/telegram') || url.includes('/auth/refresh')
      if (!isAuthRoute) {
        const refreshed = await store.dispatch('refreshToken')
        if (refreshed) {
          return fetch(url, {
            ...config,
            headers: {
              ...headers,
              Authorization: `Bearer ${store.state.token}`,
            },
          })
        }
        await handleSessionExpired()
        throw new Error('Session expired')
      }
    }
    return response
  } catch (error) {
    if (error.message === 'Session expired' || error.message === 'Not authenticated') throw error
    console.error(`API Fetch Error [${url}]:`, error)
    throw error
  }
}
